import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { folderAPI, utils } from '../utils/api';
import config from '../utils/config';
import InlinePdfViewer from '../components/InlinePdfViewer';
import InlineImageViewer from '../components/InlineImageViewer';
import FilePreview from '../components/FilePreview';
import ProgressBar from '../components/ProgressBar';
import AdUnit from '../components/AdUnit';

function FolderPage() {
  // Helper function to get file type icon
  const getFileTypeIcon = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    const iconMap = {
      // Images
      'jpg': '/icons/text_color_jpg.svg',
      'jpeg': '/icons/text_color_jpeg.svg',
      'png': '/icons/text_color_png.svg',
      'gif': '/icons/text_color_gif.svg',
      'webp': '/icons/text_color_webp.svg',
      'svg': '/icons/text_color_svg.svg',
      'ico': '/icons/text_color_ico.svg',
      'tiff': '/icons/text_color_tiff.svg',
      
      // Documents
      'pdf': '/icons/text_color_pdf.svg',
      'doc': '/icons/text_color_doc.svg',
      'txt': '/icons/text_color_txt.svg',
      'xls': '/icons/text_color_xls.svg',
      'ppt': '/icons/text_color_ppt.svg',
      
      // Archives
      'zip': '/icons/text_color_zip.svg',
      'rar': '/icons/text_color_rar.svg',
      'dmg': '/icons/text_color_dmg.svg',
      
      // Media
      'mp3': '/icons/text_color_mp3.svg',
      'mp4': '/icons/text_color_mp4.svg',
      'avi': '/icons/text_color_avi.svg',
      'mov': '/icons/text_color_mov.svg',
      'mpg': '/icons/text_color_mpg.svg',
      'wav': '/icons/text_color_wav.svg',
      
      // Code/Web
      'html': '/icons/text_color_html.svg',
      'css': '/icons/text_color_css.svg',
      'js': '/icons/text_color_js.svg',
      'json': '/icons/text_color_json.svg',
      'java': '/icons/text_color_java.svg',
      'csv': '/icons/text_color_csv.svg',
      
      // Design/Graphics
      'ai': '/icons/text_color_ai.svg',
      'psd': '/icons/text_color_psd.svg',
      'fig': '/icons/text_color_fig.svg',
      'blend': '/icons/text_color_blend.svg',
      'c4d': '/icons/text_color_c4d.svg',
      'cdr': '/icons/text_color_cdr.svg',
      'aep': '/icons/text_color_aep.svg',
      'skt': '/icons/text_color_skt.svg',
      
      // Executables
      'exe': '/icons/text_color_exe.svg'
    };
    
    return iconMap[ext] || '/icons/text_color_txt.svg'; // Default to txt icon
  };

  const { folderId } = useParams();
  const [folder, setFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());
  const [inlinePreviewFile, setInlinePreviewFile] = useState(null);
  const [resolvedPreviewType, setResolvedPreviewType] = useState(null); // 'image' | 'pdf' | null
  const [viewMode, setViewMode] = useState('overview'); // 'overview' or 'contents'
  const [folderName, setFolderName] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [prefetchedFiles, setPrefetchedFiles] = useState(null);
  const [prefetchedMeta, setPrefetchedMeta] = useState(null);
  
  // Security: Shared links are READ-ONLY - no upload functionality
  
  // Download state
  const [downloadProgress, setDownloadProgress] = useState({});
  const [showDownloadProgress, setShowDownloadProgress] = useState(false);

  // Security: Ensure this page is never indexed by search engines
  useEffect(() => {
    // Set noindex meta tags dynamically
    const setMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.getElementsByTagName('head')[0].appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Block all search engines and crawlers
    setMetaTag('robots', 'noindex, nofollow, noarchive, nosnippet, noimageindex');
    setMetaTag('googlebot', 'noindex, nofollow, noarchive, nosnippet, noimageindex');
    setMetaTag('bingbot', 'noindex, nofollow, noarchive, nosnippet');
    setMetaTag('x-robots-tag', 'noindex, nofollow, noarchive, nosnippet, noimageindex');

    // Set document title to folder name or generic title
    document.title = folderName ? `${folderName} - largetransfer.net` : 'Shared Files - largetransfer.net';

    // Cleanup function to remove sensitive data from browser history
    return () => {
      // Clear any sensitive data from window history state
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, document.title, window.location.pathname);
      }
    };
  }, []);

  // Update document title when folder name loads
  useEffect(() => {
    if (folderName) {
      document.title = `${folderName} - largetransfer.net`;
    }
  }, [folderName]);

  const loadFolderOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // For overview mode, we still need to call the API to get the folder name
      // but we won't show file details in the UI
      const data = await folderAPI.getFolder(folderId);
      
      // Extract folder name from the response (try different possible fields)
      const name = data.folder?.name || 
                   data.folder?.folderName || 
                   data.folder?.folder_name || 
                   folderId.substring(0, 8);
      
      setFolderName(name);

      // Prefetch contents in the background using optimized endpoint so click-through is instant
      try {
        const optimized = await folderAPI.getFolderFilesOptimized(folderId, 1, 100);
        if (optimized && Array.isArray(optimized.files)) {
          setPrefetchedFiles(optimized.files);
          // Keep minimal meta for header stats when available later
          setPrefetchedMeta({ totalSize: optimized.totalSize, totalDownloads: optimized.totalDownloads, createdAt: optimized.createdAt });
        }
      } catch (prefetchErr) {
        // Ignore prefetch errors; user can still click to load
        console.warn('Shared: prefetch failed', prefetchErr);
      }
      
    } catch (err) {
      console.error('Failed to load folder overview:', err);
      setError(`Failed to load folder: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [folderId]);

  const loadFolderContents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use optimized files endpoint first for speed
      let filesData = null;
      try {
        filesData = await folderAPI.getFolderFilesOptimized(folderId, 1, 500); // Increased from 100 to 500
      } catch (_) {}

      if (filesData && Array.isArray(filesData.files)) {
        // Compose a lightweight folder object; hydrate meta later
        setFolder({
          folder: {
            name: folderName || folderId.substring(0, 8),
            totalSize: filesData.totalSize || 0,
            totalDownloads: filesData.totalDownloads || 0,
            createdAt: filesData.createdAt || new Date().toISOString(),
            bandwidthUsed: filesData.bandwidthUsed || 0,
            bandwidthLimit: filesData.bandwidthLimit || (100 * 1024 * 1024 * 1024)
          },
          files: filesData.files
        });
        setViewMode('contents');

        // Hydrate full metadata in the background without blocking UI
        try {
          const fullData = await folderAPI.getFolder(folderId);
          setFolder(fullData);
        } catch (hydrateErr) {
          console.warn('Shared: hydrate meta failed', hydrateErr);
        }
      } else {
        // Fallback to full endpoint
      const data = await folderAPI.getFolder(folderId);
      setFolder(data);
      setViewMode('contents');
      }
      
    } catch (err) {
      console.error('Failed to load folder contents:', err);
      setError(`Failed to load folder: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFolderClick = () => {
    if (viewMode === 'overview') {
      // If we have prefetched files, render instantly
      if (prefetchedFiles && Array.isArray(prefetchedFiles)) {
        setFolder({
          folder: {
            name: folderName || folderId.substring(0, 8),
            totalSize: prefetchedMeta?.totalSize || 0,
            totalDownloads: prefetchedMeta?.totalDownloads || 0,
            createdAt: prefetchedMeta?.createdAt || new Date().toISOString(),
            bandwidthUsed: 0,
            bandwidthLimit: (100 * 1024 * 1024 * 1024)
          },
          files: prefetchedFiles
        });
        setViewMode('contents');
        // Hydrate in background
        (async () => {
          try {
            const fullData = await folderAPI.getFolder(folderId);
            setFolder(fullData);
          } catch (hydrateErr) {
            console.warn('Shared: hydrate meta failed', hydrateErr);
          }
        })();
      } else {
      loadFolderContents();
      }
    }
  };

  useEffect(() => {
    // Load folder overview first (just folder name and ad)
    loadFolderOverview();
  }, [folderId, loadFolderOverview]);

  const handleDownload = useCallback(async (fileId, filename, fileSize = 0) => {
    if (downloadingFiles.has(fileId)) return Promise.resolve();

    return new Promise(async (resolve, reject) => {
    try {
      setDownloadingFiles(prev => new Set(prev).add(fileId));
      // Disable custom popup; rely on browser-native download UI
      setShowDownloadProgress(false);
      
      console.log(`🔄 Starting shared folder download for: ${filename} (ID: ${fileId})`);
      
      // Use regular download endpoint (now serves files directly)
      const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
      
      // Get current user ID for self-download detection
      const currentUserId = localStorage.getItem('fileshare_user_id') || null;
      const apiUrl = currentUserId 
        ? `${apiBase}/api/download/${folderId}/${fileId}?userId=${encodeURIComponent(currentUserId)}`
        : `${apiBase}/api/download/${folderId}/${fileId}`;
      
      const res = await fetch(apiUrl);
      if (!res.ok) {
        if (res.status === 403) {
          const errorData = await res.json();
          throw new Error(errorData.message || 'Bandwidth limit exceeded');
        }
        throw new Error(`Download failed: ${res.status}`);
      }
      
      // Get the file blob from the response (direct file serving)
      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      
      // Use reliable download method
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Clean up the blob URL
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);

          resolve();
      
    } catch (err) {
      console.error('❌ Shared folder download failed:', err);
      
      // Provide specific error messages based on error type
      let userMessage = 'Download failed. Please try again.';
      if (err.message.includes('Failed to fetch')) {
        userMessage = 'Network error: Unable to reach download servers. Please check your internet connection and try again.';
      } else if (err.message.includes('CORS')) {
        userMessage = 'Browser security error: Try refreshing the page and downloading again.';
      } else if (err.message.includes('404')) {
        userMessage = 'File not found: This file may have been deleted or expired.';
      } else if (err.message.includes('403')) {
        userMessage = 'Access denied: You do not have permission to download this file.';
      } else if (err.message.includes('Bandwidth limit exceeded')) {
        userMessage = 'Download limit exceeded for this folder. The folder has reached its 100GB download limit.';
      } else if (err.message.includes('File not found')) {
        userMessage = 'File not found. It may have been deleted or expired.';
      }
      
      // Suppress blocking alert; log to console only
      console.warn(`${userMessage} | details: ${err.message}`);
      reject(err);
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        
        // Hide download progress if no more downloads
        if (newSet.size === 0) {
          setShowDownloadProgress(false);
          setDownloadProgress({});
        }
        
        return newSet;
      });
    }
    });
  }, [folderId, downloadingFiles]);

  // Helper: background download via hidden iframe (avoids multiple-gesture blocking)
  const backgroundDownload = (url) => {
    try {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);
      // Clean up later
          setTimeout(() => {
        try { document.body.removeChild(iframe); } catch (_) {}
      }, 15000);
    } catch (_) {}
  };

  // Download with progress tracking
  const downloadWithProgress = async (url, filename) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Preview functions
  const openInlinePreview = (file) => {
    setInlinePreviewFile(file);
  };

  const closeInlinePreview = () => {
    setInlinePreviewFile(null);
    setResolvedPreviewType(null);
  };

  // Resolve preview type by probing content-type from preview endpoint when needed
  useEffect(() => {
    const resolveType = async () => {
      if (!inlinePreviewFile) return;
      // If filename suggests a type, skip probe
      const name = (inlinePreviewFile.originalFilename || inlinePreviewFile.original_filename || inlinePreviewFile.filename || '').toLowerCase();
      if (name.endsWith('.pdf')) { setResolvedPreviewType('pdf'); return; }
      if (name.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/)) { setResolvedPreviewType('image'); return; }
      try {
        const fileId = inlinePreviewFile.fileId || inlinePreviewFile.file_id;
        const url = `${config.apiUrl}/api/preview/${folderId}/${fileId}`;
        const res = await fetch(url, { method: 'GET', headers: { 'Range': 'bytes=0-0' }, cache: 'no-store', credentials: 'omit' });
        const ct = res.headers.get('content-type') || '';
        if (ct.startsWith('image/')) setResolvedPreviewType('image');
        else if (ct.includes('pdf')) setResolvedPreviewType('pdf');
        else setResolvedPreviewType(null);
      } catch (e) {
        setResolvedPreviewType(null);
      }
    };
    resolveType();
  }, [inlinePreviewFile, folderId]);

  // File selection functions
  const handleFileSelect = useCallback((fileId) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedFiles.size === folder?.files?.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set((folder?.files || []).map(file => file.fileId || file.file_id)));
    }
  }, [selectedFiles.size, folder?.files?.length, folder?.files]);

  const handleBulkDownload = useCallback(async () => {
    if (selectedFiles.size === 0) return;

    try {
      setDownloadingZip(true);
      
      // Download files individually (mobile-friendly, no ZIP)
      const selectedFileObjects = folder.files.filter(file => selectedFiles.has(file.fileId));
      
      for (let i = 0; i < selectedFileObjects.length; i++) {
        const file = selectedFileObjects[i];
        
        try {
          // Add delay between downloads to avoid overwhelming the browser
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 800));
          }
          
          // Use the correct filename property
          const normalizedId = file.fileId || file.file_id;
          const filename = file.filename || file.originalFilename || file.original_filename || `file_${normalizedId}`;
          const fileSize = file.size || file.fileSize || file.file_size || 0;

          if (i === 0) {
            await handleDownload(normalizedId, filename, fileSize);
          } else {
            // Subsequent downloads: use regular download endpoint (direct file serving)
            const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
            
            // Get current user ID for self-download detection
            const currentUserId = localStorage.getItem('fileshare_user_id') || null;
            const bulkDownloadUrl = currentUserId 
              ? `${apiBase}/api/download/${folderId}/${normalizedId}?userId=${encodeURIComponent(currentUserId)}`
              : `${apiBase}/api/download/${folderId}/${normalizedId}`;
            
            const res = await fetch(bulkDownloadUrl);
            if (res.ok) {
              const blob = await res.blob();
              const downloadUrl = URL.createObjectURL(blob);
              
              // Use reliable download method
              const a = document.createElement('a');
              a.href = downloadUrl;
              a.download = filename;
              a.style.display = 'none';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              
              // Clean up the blob URL
              setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
            }
          }
          
        } catch (fileError) {
          console.error(`Failed to download ${file.filename || file.fileId}:`, fileError);
          // Continue with other files
        }
      }

      // Clear selection after download
      setSelectedFiles(new Set());
      
    } catch (err) {
      console.error('Bulk download failed:', err);
      alert(`Download failed: ${err.message}. Please try again.`);
    } finally {
      setDownloadingZip(false);
    }
  }, [selectedFiles, folder?.files, handleDownload]);



  // Check if file type is previewable
  const isPreviewable = (file) => {
    if (!file) return false;
    
    const mimeType = file.mimeType || file.contentType || file.mime_type || '';
    const filename = (file.filename || file.originalFilename || file.original_filename || '').toLowerCase();
    
    return (
      // Images
      mimeType.startsWith('image/') ||
      filename.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/) ||
      // PDFs
      mimeType === 'application/pdf' || 
      filename.endsWith('.pdf') ||
      // Text files
      mimeType.startsWith('text/') ||
      mimeType === 'application/json' ||
      mimeType === 'application/javascript' ||
      mimeType === 'application/xml' ||
      filename.match(/\.(txt|md|json|csv|log|ini|conf|yaml|yml|js|css|html|xml|py|java|cpp|c|h|sh|bat)$/)
    );
  };

  // Avoid blocking loader on initial overview. Only show full-screen loader when switching
  // to contents and there is no prefetched data available.
  if (loading && viewMode === 'contents' && !folder) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#1a1d29', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '32px', 
            height: '32px', 
            border: '2px solid #60a5fa', 
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            margin: '0 auto 16px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#9ca3af', fontSize: '12px' }}>Loading folder contents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#1a1d29', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', margin: '0 auto', padding: '0 16px' }}>
          <div style={{ 
            backgroundColor: '#ef444420', 
            borderRadius: '50%', 
            width: '48px', 
            height: '48px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            margin: '0 auto 16px',
            border: '1px solid #ef444440'
          }}>
            <svg width="24" height="24" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#e2e8f0', marginBottom: '8px' }}>Folder Not Found</h2>
          <p style={{ color: '#9ca3af', marginBottom: '24px', fontSize: '12px' }}>{error}</p>
          <Link 
            to="/"
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '2px',
              fontSize: '12px',
              fontWeight: '500',
              textDecoration: 'none',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            Upload New File
          </Link>
        </div>
      </div>
    );
  }

  const bandwidthPercentage = folder?.folder ? (folder.folder.bandwidthUsed / folder.folder.bandwidthLimit) * 100 : 0;

  // Check for copyright violation
  if (folder?.copyrightViolation || 
      folder?.folder?.name === 'DELETED_COPYRIGHT_VIOLATION' || 
      folder?.folder?.folderName === 'DELETED_COPYRIGHT_VIOLATION' || 
      folder?.folder?.folder_name?.startsWith('DELETED_COPYRIGHT_VIOLATION') ||
      folderName === 'DELETED_COPYRIGHT_VIOLATION') {
    
    const takedownReason = folder?.takedownReason || 'Copyright violation';
    
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#1a1d29', color: '#e2e8f0', padding: '20px' }}>
        {/* Fixed overlay modal similar to delete confirmation */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '24px',
            width: '400px',
            maxWidth: '90vw',
            textAlign: 'center',
            position: 'relative'
          }}>
            {/* Warning Icon - similar to delete modal but red */}
            <div style={{
              backgroundColor: '#fed7d7',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#FF0000' }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
                <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>

            <h3 style={{ 
              color: '#e2e8f0', 
              fontSize: '18px', 
              fontWeight: '500',
              marginBottom: '16px' 
            }}>
              Content Removed
            </h3>
            
            <p style={{ 
              color: '#9ca3af', 
              fontSize: '14px', 
              marginBottom: '16px',
              lineHeight: '1.5' 
            }}>
              This content has been removed due to copyright infringement.
            </p>

            <div style={{
              backgroundColor: '#374151',
              border: '1px solid #4b5563',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              <div style={{ 
                color: '#f87171', 
                fontSize: '12px', 
                fontWeight: '500',
                marginBottom: '4px' 
              }}>
                Reason for Takedown:
              </div>
              <div style={{ 
                color: '#e2e8f0', 
                fontSize: '14px',
                lineHeight: '1.4'
              }}>
                {takedownReason}
              </div>
            </div>
            
            <button
              onClick={() => window.location.href = '/'}
              style={{
                backgroundColor: '#4b5563',
                color: '#e2e8f0',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '2px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#6b7280'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#4b5563'}
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render folder overview (just folder name)
  if (viewMode === 'overview') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#1a1d29' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
          {/* Header */}
          <div style={{ padding: '24px 0', marginBottom: '24px' }}>
            <Link 
              to="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                color: '#60a5fa',
                textDecoration: 'none',
                marginBottom: '16px',
                fontSize: '11px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
              onMouseLeave={(e) => e.target.style.color = '#60a5fa'}
            >
              <svg width="12" height="12" style={{ marginRight: '6px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Upload
            </Link>

            <h1 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#e2e8f0', 
              marginBottom: '8px' 
            }}>
              Shared Content
            </h1>
          </div>

          {/* Folder Name Display */}
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px',
                backgroundColor: '#242938'
              }}
            >
              {/* Folder Icon */}
              <div style={{ 
                backgroundColor: '#1a1d29', 
                borderRadius: '2px', 
                padding: '8px', 
                marginRight: '12px',
                color: '#6b7280'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"/>
                </svg>
              </div>

              {/* Folder Info */}
              <div style={{ flex: '1', minWidth: '0' }}>
                <button
                  onClick={handleFolderClick}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#e2e8f0',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textDecorationColor: 'transparent',
                    transition: 'all 0.2s ease',
                    padding: '0',
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#60a5fa';
                    e.target.style.textDecorationColor = '#60a5fa';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#e2e8f0';
                    e.target.style.textDecorationColor = 'transparent';
                  }}
                >
                  {folderName || 'Folder'}
                </button>
                <div style={{ 
                  fontSize: '11px', 
                  color: '#9ca3af',
                  marginTop: '4px'
                }}>
                  Click to view contents
                </div>
              </div>
            </div>
          </div>

          {/* Google Ads Unit - SAFE for AdSense compliance: overview page before user sees files */}
          <AdUnit slotType="folder" style={{ marginTop: '24px' }} />
        </div>
      </div>
    );
  }

  // Render full folder contents view
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#1a1d29' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
        {/* Header */}
        <div style={{ padding: '24px 0', marginBottom: '24px' }}>
          <Link 
            to="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: '#60a5fa',
              textDecoration: 'none',
              marginBottom: '16px',
              fontSize: '11px',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
            onMouseLeave={(e) => e.target.style.color = '#60a5fa'}
          >
            <svg width="12" height="12" style={{ marginRight: '6px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Upload
          </Link>

          <h1 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#e2e8f0', 
            marginBottom: '8px' 
          }}>
            {folderName || 'Shared Files'}
          </h1>
          
          {/* Removed stats row per request */}
        </div>

        {/* Bandwidth Usage */}
        {folder?.folder && bandwidthPercentage > 50 && (
          <div style={{
            backgroundColor: bandwidthPercentage > 90 ? '#2d1b1b' : '#1e2a32',
            border: `1px solid ${bandwidthPercentage > 90 ? '#3e2222' : '#2a3441'}`,
            borderRadius: '2px',
            padding: '12px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ 
                fontSize: '11px', 
                fontWeight: '500', 
                color: bandwidthPercentage > 90 ? '#f87171' : '#60a5fa' 
              }}>
                Download Bandwidth
              </span>
              <span style={{ 
                fontSize: '11px', 
                color: bandwidthPercentage > 90 ? '#f87171' : '#60a5fa' 
              }}>
                {utils.formatFileSize(folder.folder.bandwidthUsed)} / {utils.formatFileSize(folder.folder.bandwidthLimit)}
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '4px',
              backgroundColor: '#374151',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${Math.min(bandwidthPercentage, 100)}%`,
                height: '100%',
                backgroundColor: bandwidthPercentage > 90 ? '#ef4444' : '#3b82f6',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
            {bandwidthPercentage > 90 && (
              <p style={{ fontSize: '10px', color: '#f87171', marginTop: '8px', margin: '8px 0 0 0' }}>
                Download limit nearly reached. New downloads may be restricted.
              </p>
            )}
          </div>
        )}

        {/* Bulk Selection Controls */}
        {folder?.files && folder.files.length > 0 && (
          <div style={{
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '2px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '40px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', whiteSpace: 'nowrap' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '11px',
                color: '#e2e8f0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={selectedFiles.size === folder.files.length && folder.files.length > 0}
                  onChange={handleSelectAll}
                  style={{ cursor: 'pointer' }}
                />
                Select All ({folder.files.length} files)
              </label>
              {selectedFiles.size > 0 && (
                <span style={{ fontSize: '11px', color: '#60a5fa' }}>
                  {selectedFiles.size} selected
                </span>
              )}
            </div>
            
            {selectedFiles.size > 0 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleBulkDownload}
                  disabled={downloadingZip}
                  style={{
                    backgroundColor: downloadingZip ? '#374151' : '#10b981',
                    color: 'white',
                    border: 'none',
                    padding: '0 12px',
                    height: '24px',
                    lineHeight: '24px',
                    borderRadius: '2px',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: downloadingZip ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    if (!downloadingZip) {
                      e.target.style.backgroundColor = '#059669';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!downloadingZip) {
                      e.target.style.backgroundColor = '#10b981';
                    }
                  }}
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {downloadingZip ? 'Downloading...' : `Download (${selectedFiles.size})`}
                </button>
                

              </div>
            )}
          </div>
        )}

        {/* Files List */}
        {loading && viewMode === 'contents' ? (
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            overflow: 'hidden',
            padding: '12px 16px'
          }}>
            {[1,2,3,4].map((i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
                <div style={{ width: '20px', height: '20px', backgroundColor: '#1f2937', borderRadius: '2px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ height: '10px', backgroundColor: '#1f2937', borderRadius: '2px', marginBottom: '6px', width: '60%' }} />
                  <div style={{ height: '8px', backgroundColor: '#1f2937', borderRadius: '2px', width: '30%' }} />
                </div>
                <div style={{ width: '32px', height: '32px', backgroundColor: '#1f2937', borderRadius: '3px' }} />
                <div style={{ width: '32px', height: '32px', backgroundColor: '#1f2937', borderRadius: '3px' }} />
                <div style={{ width: '32px', height: '32px', backgroundColor: '#1f2937', borderRadius: '3px' }} />
              </div>
            ))}
          </div>
        ) : folder?.files && folder.files.length > 0 ? (
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            {folder.files.map((file, index) => (
              <div
                key={file.fileId || file.file_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px',
                  borderBottom: index < folder.files.length - 1 ? '1px solid #374151' : 'none',
                  backgroundColor: selectedFiles.has(file.fileId || file.file_id) ? '#1e293b' : '#242938',
                  transition: 'background-color 0.2s ease'
                }}
                onClick={() => {
                  const isActive = inlinePreviewFile && inlinePreviewFile.fileId === (file.fileId || file.file_id);
                  if (isPreviewable(file)) {
                    if (isActive) {
                      closeInlinePreview();
                    } else {
                      openInlinePreview(file);
                    }
                  }
                }}
              >
                {/* Selection Checkbox */}
                <div style={{ marginRight: '12px' }}>
                  <input
                    type="checkbox"
                    checked={selectedFiles.has(file.fileId || file.file_id)}
                    onChange={() => handleFileSelect(file.fileId || file.file_id)}
                    style={{ 
                      cursor: 'pointer',
                      transform: 'scale(1.1)'
                    }}
                  />
                </div>

                {/* File Icon */}
                <div style={{ 
                  backgroundColor: '#1a1d29', 
                  borderRadius: '2px', 
                  padding: '8px', 
                  marginRight: '12px',
                  color: '#6b7280'
                }}>
                  {(() => { 
                    const filename = file.filename || file.originalFilename || file.original_filename || `file_${file.fileId}`;
                    const iconPath = getFileTypeIcon(filename);
                    return (
                      <img 
                        src={iconPath} 
                        alt={filename}
                        style={{
                          width: '20px',
                          height: '20px',
                          objectFit: 'contain',
                          pointerEvents: 'none',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          MozUserSelect: 'none',
                          msUserSelect: 'none',
                          WebkitTouchCallout: 'none',
                          WebkitUserDrag: 'none',
                          KhtmlUserSelect: 'none'
                        }}
                        draggable={false}
                        onError={(e) => {
                          // Fallback to default icon if SVG fails to load
                          e.target.src = '/icons/text_color_txt.svg';
                        }}
                      />
                    );
                  })()}
                </div>

                {/* File Info */}
                <div style={{ flex: '1', minWidth: '0' }}>
                  <div style={{ 
                    fontSize: '13px', 
                    fontWeight: '500', 
                    color: '#e2e8f0',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginBottom: '4px'
                  }}>
                    {file.filename || file.originalFilename || file.original_filename || `file_${file.fileId || file.file_id}`}
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#9ca3af',
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <span>{utils.formatFileSize(file.size || file.fileSize || file.file_size || 0)}</span>
                    {(file.downloadCount || file.download_count || 0) > 0 && (
                      <>
                        <span>•</span>
                        <span>{file.downloadCount || file.download_count} download{(file.downloadCount || file.download_count) !== 1 ? 's' : ''}</span>
                      </>
                    )}
                    <span>•</span>
                    <span>Uploaded {utils.formatDate(file.uploadDate || file.upload_date || new Date().toISOString())}</span>
                  </div>
                </div>

                {/* Actions - Download only for shared view */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {/* Download */}
                  <button
                    onClick={() => handleDownload((file.fileId || file.file_id), file.filename || file.originalFilename || file.original_filename || `file_${file.fileId || file.file_id}`, file.size || file.fileSize || file.file_size || 0)}
                    disabled={downloadingFiles.has(file.fileId || file.file_id)}
                    title="Download"
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.6)',
                      backdropFilter: 'blur(6px)',
                      color: '#e2e8f0',
                      border: '1px solid #334155',
                      width: '32px',
                      height: '32px',
                      borderRadius: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: downloadingFiles.has(file.fileId || file.file_id) ? 0.6 : 1
                    }}
                  >
                    {downloadingFiles.has(file.fileId || file.file_id) ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }}>
                        <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z" opacity="0.3"/>
                        <path d="M12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8V2z"/>
                      </svg>
                    ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 3v10.17l3.59-3.58L17 11l-5 5-5-5 1.41-1.41L11 13.17V3h1z"/>
                      <path d="M5 18h14v2H5z"/>
                    </svg>
                    )}
                  </button>
                </div>
              </div>
            ))
            
            // Add inline PDF viewer after the file that's being previewed
            .reduce((acc, fileElement, index) => {
              acc.push(fileElement);
              
              // Check if this file is being previewed inline
              const file = folder.files[index];
              if (inlinePreviewFile && (file.fileId || file.file_id) === inlinePreviewFile.fileId) {
                // Determine file type for proper preview component (use resolved type if available)
                const contentType = file.contentType || file.content_type || file.mime_type || '';
                const filename = (file.originalFilename || file.original_filename || file.filename || '').toLowerCase();
                const isPDF = resolvedPreviewType ? resolvedPreviewType === 'pdf' : (contentType === 'application/pdf' || filename.endsWith('.pdf'));
                const isImage = resolvedPreviewType ? resolvedPreviewType === 'image' : (contentType.startsWith('image/') || filename.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/));
                
                acc.push(
                  <div key={`preview-${file.fileId || file.file_id}`} style={{ padding: '0 16px' }}>
                    {isPDF ? (
                      <InlinePdfViewer
                        file={inlinePreviewFile}
                        folderId={folderId}
                        onClose={closeInlinePreview}
                      />
                    ) : isImage ? (
                      <InlineImageViewer
                        file={inlinePreviewFile}
                        folderId={folderId}
                        onClose={closeInlinePreview}
                      />
                    ) : (
                      <div style={{
                        backgroundColor: '#1f2937',
                        border: '1px dashed #374151',
                        borderRadius: '4px',
                        height: '320px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#9ca3af',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          border: '3px solid #4b5563',
                          borderTopColor: '#60a5fa',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}/>
                        <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
                      </div>
                    )}
                  </div>
                );
              }
              
              return acc;
            }, [])
            }
          </div>
        ) : (
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '48px',
            textAlign: 'center'
          }}>
            <div style={{ color: '#6b7280', marginBottom: '16px' }}>
              <svg width="48" height="48" style={{ margin: '0 auto' }} viewBox="0 0 16 16" fill="currentColor">
                <path d="M9.5 1H3a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V4.5L9.5 1ZM3 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4.293a1 1 0 0 0-.293-.707l-4-4A1 1 0 0 0 10 3H3Z"/>
              </svg>
            </div>
            <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0', marginBottom: '8px' }}>
              No Files Found
            </h3>
            <p style={{ fontSize: '12px', color: '#9ca3af' }}>
              This folder appears to be empty or the files may have expired.
            </p>
          </div>
        )}

        {/* No upload functionality - shared links are read-only */}

      </div>

      {/* Download Progress Modal removed */}
      
      {/* CSS for download spinner animation */}
          <style>{`
            @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
            }
          `}</style>

    </div>
  );
}

export default FolderPage; 